// AI企业诊断组件
import React, { useState } from 'react';
import { Sparkles, X, Check, ArrowRight, Building2, DollarSign, Users, AlertCircle, Send, Phone, Mail } from 'lucide-react';

// 企业类型选项
const companyTypes = [
  { value: 'manufacturing', label: '制造业' },
  { value: 'retail', label: '零售业' },
  { value: 'service', label: '服务业' },
  { value: 'tech', label: '科技/互联网' },
  { value: 'finance', label: '金融业' },
  { value: 'logistics', label: '物流运输' },
  { value: 'other', label: '其他' }
];

// 年收入选项
const revenueRanges = [
  { value: 'under1m', label: '100万以下' },
  { value: '1m-5m', label: '100万-500万' },
  { value: '5m-10m', label: '500万-1000万' },
  { value: '10m-50m', label: '1000万-5000万' },
  { value: '50mplus', label: '5000万以上' }
];

// 员工规模选项
const employeeRanges = [
  { value: 'under10', label: '10人以下' },
  { value: '10-50', label: '10-50人' },
  { value: '50-200', label: '50-200人' },
  { value: '200-500', label: '200-500人' },
  { value: '500plus', label: '500人以上' }
];

// 痛点选项
const painPointOptions = [
  { value: 'cost', label: '降本增效', icon: '💰' },
  { value: 'repetitive', label: '重复工作多', icon: '🔄' },
  { value: 'decision', label: '决策困难', icon: '🤔' },
  { value: 'data', label: '数据混乱', icon: '📊' },
  { value: 'talent', label: '人才流失', icon: '👥' },
  { value: 'efficiency', label: '效率低下', icon: '🐢' }
];

// 基于规则的诊断建议生成
const generateDiagnosis = (formData) => {
  const suggestions = [];
  const recommendedServices = [];

  // 根据企业类型推荐
  if (formData.companyType === 'manufacturing') {
    suggestions.push({
      title: '智能资产管理',
      content: '制造业固定资产众多，建议部署AI智能盘点系统，效率可提升10倍。',
      priority: 'high'
    });
    recommendedServices.push('AI智能盘点');
  }

  if (formData.companyType === 'service' || formData.companyType === 'retail') {
    suggestions.push({
      title: '智能客服部署',
      content: '服务业客户咨询量大，建议使用智能客服助手，降低人力成本50%。',
      priority: 'high'
    });
    recommendedServices.push('智能客服助手');
  }

  // 根据痛点推荐
  if (formData.painPoints.includes('repetitive')) {
    suggestions.push({
      title: '流程自动化',
      content: '建议部署报表自动生成和会议智能纪要功能，释放重复劳动时间。',
      priority: 'medium'
    });
    recommendedServices.push('报表自动生成', '会议智能纪要');
  }

  if (formData.painPoints.includes('talent')) {
    suggestions.push({
      title: '知识经验沉淀',
      content: '建议构建企业知识库，实现员工离职经验零流失，新员工培训周期缩短50%。',
      priority: 'high'
    });
    recommendedServices.push('员工经验沉淀');
  }

  if (formData.painPoints.includes('decision')) {
    suggestions.push({
      title: '数据驱动决策',
      content: '建议构建企业情景图谱，让数据资产辅助业务决策，从"经验决策"升级为"数据决策"。',
      priority: 'medium'
    });
  }

  if (formData.painPoints.includes('cost')) {
    suggestions.push({
      title: '全面降本方案',
      content: 'AI技术可帮助企业平均降低20%运营成本，建议先从高频重复场景入手。',
      priority: 'high'
    });
  }

  // 根据规模推荐
  if (formData.employeeCount === 'under10' || formData.employeeCount === '10-50') {
    suggestions.push({
      title: '轻量化快速部署',
      content: '小规模企业建议从单一场景切入，如智能客服或报表自动化，见效快、投入低。',
      priority: 'medium'
    });
  }

  // 默认建议
  if (suggestions.length === 0) {
    suggestions.push({
      title: '免费AI诊断服务',
      content: '提交成功！我们的专家将在24小时内与您联系，提供免费诊断咨询服务。',
      priority: 'medium'
    });
  }

  return { suggestions, recommendedServices };
};

// 诊断结果展示组件
const DiagnosisResult = ({ formData, onReset, onConsult }) => {
  const { suggestions, recommendedServices } = generateDiagnosis(formData);

  return (
    <div className="text-center">
      <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-[0_0_30px_rgba(16,185,129,0.4)]">
        <Sparkles className="w-10 h-10 text-white" />
      </div>
      
      <h3 className="text-2xl font-bold text-white mb-2">诊断完成！</h3>
      <p className="text-slate-400 mb-8">基于您的企业信息，我们给出以下分析建议</p>

      <div className="text-left space-y-4 mb-8">
        {suggestions.map((suggestion, idx) => (
          <div 
            key={idx} 
            className={`p-4 rounded-xl border ${
              suggestion.priority === 'high' 
                ? 'bg-blue-900/20 border-blue-500/30' 
                : 'bg-slate-800/50 border-slate-700'
            }`}
          >
            <div className="flex items-start gap-3">
              <div className={`w-2 h-2 rounded-full mt-2 ${
                suggestion.priority === 'high' ? 'bg-blue-400' : 'bg-slate-500'
              }`}></div>
              <div>
                <h4 className="font-bold text-white mb-1">{suggestion.title}</h4>
                <p className="text-sm text-slate-400">{suggestion.content}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {recommendedServices.length > 0 && (
        <div className="mb-8">
          <p className="text-sm text-slate-500 mb-3">推荐服务</p>
          <div className="flex flex-wrap justify-center gap-2">
            {recommendedServices.map((service, idx) => (
              <span key={idx} className="px-4 py-2 bg-cyan-900/30 border border-cyan-500/30 text-cyan-300 rounded-full text-sm">
                {service}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-4">
        <button 
          onClick={onConsult}
          className="flex-1 py-4 bg-white text-[#070b14] font-bold rounded-full hover:bg-slate-200 transition-colors flex items-center justify-center gap-2"
        >
          <Phone className="w-5 h-5" />
          立即咨询
        </button>
        <button 
          onClick={onReset}
          className="px-8 py-4 border border-slate-600 text-slate-300 rounded-full hover:bg-slate-800 transition-colors"
        >
          再测一次
        </button>
      </div>
    </div>
  );
};

// AI诊断表单组件
export default function AIDiagnosis({ isOpen, onClose }) {
  const [step, setStep] = useState(1); // 1: 表单, 2: 结果
  const [formData, setFormData] = useState({
    companyName: '',
    companyType: '',
    annualRevenue: '',
    employeeCount: '',
    painPoints: [],
    contactPhone: '',
    contactEmail: ''
  });
  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handlePainPointToggle = (value) => {
    setFormData(prev => ({
      ...prev,
      painPoints: prev.painPoints.includes(value)
        ? prev.painPoints.filter(p => p !== value)
        : [...prev.painPoints, value]
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.companyType) newErrors.companyType = '请选择企业类型';
    if (!formData.annualRevenue) newErrors.annualRevenue = '请选择年收入';
    if (!formData.employeeCount) newErrors.employeeCount = '请选择员工规模';
    if (formData.painPoints.length === 0) newErrors.painPoints = '请选择至少一个痛点';
    if (!formData.contactPhone) newErrors.contactPhone = '请填写联系电话';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      setStep(2);
    }
  };

  const handleReset = () => {
    setStep(1);
    setFormData({
      companyName: '',
      companyType: '',
      annualRevenue: '',
      employeeCount: '',
      painPoints: [],
      contactPhone: '',
      contactEmail: ''
    });
  };

  const handleConsult = () => {
    // 这里可以触发拨打电话或打开咨询弹窗
    alert('感谢您的兴趣！我们的顾问将尽快与您联系。');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose}></div>
      
      <div className="relative bg-[#0b101d] border border-slate-700 rounded-3xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-white p-2">
          <X className="w-6 h-6" />
        </button>

        <div className="p-8">
          {step === 1 ? (
            <>
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-[0_0_25px_rgba(139,92,246,0.4)]">
                  <Sparkles className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white">免费企业AI诊断</h3>
                <p className="text-sm text-slate-400 mt-2">填写信息，获得专属AI落地建议</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* 企业名称 */}
                <div>
                  <label className="block text-sm text-slate-400 mb-2">企业名称（选填）</label>
                  <input
                    type="text"
                    value={formData.companyName}
                    onChange={(e) => handleInputChange('companyName', e.target.value)}
                    placeholder="请输入企业名称"
                    className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:border-indigo-500 focus:outline-none transition-colors"
                  />
                </div>

                {/* 企业类型 */}
                <div>
                  <label className="block text-sm text-slate-400 mb-2">
                    企业类型 <span className="text-red-400">*</span>
                  </label>
                  <select
                    value={formData.companyType}
                    onChange={(e) => handleInputChange('companyType', e.target.value)}
                    className={`w-full px-4 py-3 bg-slate-800/50 border rounded-xl text-white focus:outline-none transition-colors ${
                      errors.companyType ? 'border-red-500' : 'border-slate-700 focus:border-indigo-500'
                    }`}
                  >
                    <option value="">请选择企业类型</option>
                    {companyTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                  {errors.companyType && <p className="text-red-400 text-xs mt-1">{errors.companyType}</p>}
                </div>

                {/* 年收入 */}
                <div>
                  <label className="block text-sm text-slate-400 mb-2">
                    年收入 <span className="text-red-400">*</span>
                  </label>
                  <select
                    value={formData.annualRevenue}
                    onChange={(e) => handleInputChange('annualRevenue', e.target.value)}
                    className={`w-full px-4 py-3 bg-slate-800/50 border rounded-xl text-white focus:outline-none transition-colors ${
                      errors.annualRevenue ? 'border-red-500' : 'border-slate-700 focus:border-indigo-500'
                    }`}
                  >
                    <option value="">请选择年收入范围</option>
                    {revenueRanges.map(range => (
                      <option key={range.value} value={range.value}>{range.label}</option>
                    ))}
                  </select>
                  {errors.annualRevenue && <p className="text-red-400 text-xs mt-1">{errors.annualRevenue}</p>}
                </div>

                {/* 员工规模 */}
                <div>
                  <label className="block text-sm text-slate-400 mb-2">
                    员工人数 <span className="text-red-400">*</span>
                  </label>
                  <select
                    value={formData.employeeCount}
                    onChange={(e) => handleInputChange('employeeCount', e.target.value)}
                    className={`w-full px-4 py-3 bg-slate-800/50 border rounded-xl text-white focus:outline-none transition-colors ${
                      errors.employeeCount ? 'border-red-500' : 'border-slate-700 focus:border-indigo-500'
                    }`}
                  >
                    <option value="">请选择员工规模</option>
                    {employeeRanges.map(range => (
                      <option key={range.value} value={range.value}>{range.label}</option>
                    ))}
                  </select>
                  {errors.employeeCount && <p className="text-red-400 text-xs mt-1">{errors.employeeCount}</p>}
                </div>

                {/* 痛点 */}
                <div>
                  <label className="block text-sm text-slate-400 mb-2">
                    当前痛点 <span className="text-red-400">*</span>（多选）
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {painPointOptions.map(option => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => handlePainPointToggle(option.value)}
                        className={`p-3 rounded-xl border text-left transition-all ${
                          formData.painPoints.includes(option.value)
                            ? 'bg-indigo-900/30 border-indigo-500 text-white'
                            : 'bg-slate-800/30 border-slate-700 text-slate-400 hover:border-slate-600'
                        }`}
                      >
                        <span className="mr-2">{option.icon}</span>
                        {option.label}
                      </button>
                    ))}
                  </div>
                  {errors.painPoints && <p className="text-red-400 text-xs mt-1">{errors.painPoints}</p>}
                </div>

                {/* 联系方式 */}
                <div>
                  <label className="block text-sm text-slate-400 mb-2">
                    联系电话 <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="tel"
                    value={formData.contactPhone}
                    onChange={(e) => handleInputChange('contactPhone', e.target.value)}
                    placeholder="请输入手机号"
                    className={`w-full px-4 py-3 bg-slate-800/50 border rounded-xl text-white placeholder-slate-500 focus:outline-none transition-colors ${
                      errors.contactPhone ? 'border-red-500' : 'border-slate-700 focus:border-indigo-500'
                    }`}
                  />
                  {errors.contactPhone && <p className="text-red-400 text-xs mt-1">{errors.contactPhone}</p>}
                </div>

                {/* 邮箱 */}
                <div>
                  <label className="block text-sm text-slate-400 mb-2">邮箱（选填）</label>
                  <input
                    type="email"
                    value={formData.contactEmail}
                    onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                    placeholder="请输入邮箱"
                    className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:border-indigo-500 focus:outline-none transition-colors"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold rounded-full hover:from-indigo-500 hover:to-purple-500 transition-all shadow-[0_0_20px_rgba(139,92,246,0.3)] flex items-center justify-center gap-2"
                >
                  <Send className="w-5 h-5" />
                  提交诊断
                </button>
              </form>
            </>
          ) : (
            <DiagnosisResult 
              formData={formData} 
              onReset={handleReset}
              onConsult={handleConsult}
            />
          )}
        </div>
      </div>
    </div>
  );
}